import {
  ILccaActivityImperial,
  ILccaAlternativeImperial,
  ILccaCalculated,
  ILccaCalculatedImperial,
} from '@/modules/lcca/interfaces/lccaCalculated';
import { IActivityResultBreakdown, IAlternativeResult } from '@/modules/lcca/interfaces/probabilistic';
// import {plotDeterministicResults} from "@/modules/lcca/lib/deterministicResultChart";
import { calculateAgencyCost } from '@/modules/lcca/lib/calculations/agencyCost';
import { calculateUserCosts } from '@/modules/lcca/lib/calculations/trafficCalculations';
import { DistributionTypes, IDistribution } from '@/lib/monteCarlo/interfaces';
import { sampleDistribution } from '@/lib/monteCarlo/pdf';
// @ts-ignore


export const getAvailableProbVariables = function (scenario: ILccaCalculated): IDistribution[] {
  const probabilisticVariables = 0;
  const pVariables: IDistribution[] = [];

  scenario.probabilistic.forEach((P) => {
    if (P.prob !== DistributionTypes.deterministic) {
      pVariables.push(P);
    }
  });

  return pVariables;
};


export const mainLccaCalculation = function (scenario: ILccaCalculatedImperial, pVariables: IDistribution[]) {
  const probabilisticUserCosts = false;
  const userCostsResults = null;

  const { alternatives } = scenario;

  /*
  Possibly probabilistic variables associated with the scenario itself
   */
  const scenarioOptions: { [key: string]: IDistribution | null } = {
    beginningAnalysisPeriod: null,
    analysisPeriod: null,
    alternatives: null,
    discountRate: null,
    userCostsInAnalysis: null,
    userCostComputationMethod: null,
  };

  /*
  Possibly probabilistic variables associated with activities
   */
  const userProbVariables: { [key: string]: IDistribution | null } = {
    annualGrowthTraffic: null,
    trafficBreakdown: null,
    singleTruckAADT: null,
    combinationTruckAADT: null,
    aadtAnalysisPeriod: null,
    speedLimitNormalOpt: null,
    freeFlowCapacity: null,
    queueDissipationCapacity: null,
    analysisPeriod: null,
    passengerCarsTime: null,
    singleUnitTrucksTime: null,
    combinationTrucksTime: null,
    trafficHourlyDistribution: null,
    lanesNormalOpt: null,
  };

  const agencyProbVariables: { [key: string]: IDistribution | null } = {
    discountRate: null,
    userCostsInAnalysis: null,
  };

  /*
  Create an object to hold the possible probabilistic variables given the use of user costs or not.
   */

  console.log('User cost & computation', scenario.userCostsInAnalysis, scenario.userCostComputationMethod);
  let activityOptions: { [key: string]: IDistribution | null } = {};
  if (scenario.userCostsInAnalysis && scenario.userCostComputationMethod === 'Calculated') {
    activityOptions = Object.assign(activityOptions, userProbVariables);
    activityOptions = Object.assign(activityOptions, agencyProbVariables);
  } else {
    activityOptions = agencyProbVariables;
  }

  // Does the below code address probabilisitic variables that aren't in traffic data, when userCostsInAnalysis isn't selected?
  // Also, does it handle when user costs are specified?


  pVariables.forEach((v) => {
    console.log(v.name, Object.keys(activityOptions).filter((o) => v.name === o).length > 0);
  });


  /*
  If there is a probability distribution for the variable, sample the distribution.
  Otherwise, collect the deterministic value.
   */
  Object.keys(activityOptions).forEach((variable) => {
    // console.log(variable);

    // See if variable is in the list of available probabilistic variables
    const pv = pVariables.filter((v) => v.name === variable);

    // Throw error if there's duplicates of the same entry
    if (pv.length > 1) {
      throw new Error(`Duplicate entry in scenario probability: ${pv.map((v) => v.name).toString()}`);
    }

    // If there's one or zero entries, grab the data
    if (pv.length > 0) {
      // eslint-disable-next-line
      scenarioOptions[variable] = pv[0];
    } else {
      // Otherwise grab the deterministic value from the scenario
      // @ts-ignore
      scenarioOptions[variable] = scenario[variable] ? scenario[variable] : null;
    }

    // console.log('options', activityOptions)
  });

  // console.log('activityOptions2', activityOptions);

  return { scenario: scenarioOptions, activity: activityOptions };
};


export const evaluateAlternative = function (alternative: ILccaAlternativeImperial,
                                             activityOptions: any,
                                             probabilisticVariables: IDistribution[],
                                             scenario: ILccaCalculatedImperial): IAlternativeResult {
  // Uses activity interface, but is for whole alternative
  // const totalActivityResults: IActivityResultBreakdown = {
  //   totalCosts: {cars: 0, trucks: 0, comboTrucks: 0},
  //   result: null,
  //   name: alternative.name + 'result breakdown',
  //   activity: null
  // };

  const altResult: IAlternativeResult = {
    alternative,
    activityResults: [],
    activityOptions: JSON.parse(JSON.stringify(activityOptions)), // / Gotta be a better way
    agencyCosts: 0,
    userCosts: 0,
    delayCosts: 0,
    vehicleCosts: 0,
    vehicleCostsByType: {
      cars: 0,
      trucks: 0,
      comboTrucks: 0,
    },
    queueLengthWarning: false,
    monteCarloProcessedData: null,
    monteCarloSortedCosts: null,
  };

  const categoryBreakdown = function (activityResults: IActivityResultBreakdown[],
                                      vehicleType: 'cars' | 'trucks' | 'comboTrucks'): number {
    // console.log('activityres', activityResults);
    return activityResults.map((A) => A.totalCosts[vehicleType]).reduce((a, b) => a + b, 0);
  };


  // Store results for each activity
  const activityResults: IActivityResultBreakdown[] = []; // array to access each alternative breakdown

  const userDiscountedResults = 0;

  /*
  Calculate agency costs
   */

  // Add up agency costs:
  alternative.activities.forEach((activity: ILccaActivityImperial) => {
    altResult.agencyCosts += calculateAgencyCost(activity.lineItems);
  });

  /*
  Calculate user costs
   */

  if (scenario.userCostsInAnalysis && scenario.userCostComputationMethod === 'Calculated') {
    // Add together costs from each activity
    alternative.activities.forEach((activity: ILccaActivityImperial) => {
      // Sample probabilistic distributions if they exist
      Object.keys(activityOptions).forEach((variable) => {
        // See if variable is in the list of available probabilistic variables
        const pv = probabilisticVariables.filter((v) => v.name === variable);

        // Throw error if there's duplicates of the same entry
        if (pv.length > 1) {
          throw new Error(`Duplicate entry in activity probability: ${pv.map((v) => v.name).toString()}`);
        }

        // If there's one or zero entries, grab the data
        if (pv.length > 0) {
          const sampleValue = sampleDistribution(pv[0]);
          // console.log("Sampling", pv[0].name, sampleValue);
          altResult.activityOptions[variable] = sampleValue;
        } else {
          // Otherwise grab the deterministic value from the scenario
          // console.log("not sampling", variable);
          // @ts-ignore
          altResult.activityOptions[variable] = scenario[variable] ? scenario[variable] : null;
        }
      });


      const activityUserCostResults = calculateUserCosts(
        scenario,
        altResult.activityOptions,
        activity,
      );

      if (activityUserCostResults.queueLengthWarning) {
        altResult.queueLengthWarning = true;
      }

      altResult.delayCosts += (activityUserCostResults.delayCostsTotal / 1000);
      altResult.vehicleCosts += (activityUserCostResults.vehicleCostsTotal / 1000);
      altResult.userCosts += (activityUserCostResults.totalCosts.total / 1000);
      // altResult.userDiscountedResults += (activity.activityResults.discountedCosts.total / 1000);

      /*

      Calculate the costs of a single activity

       */
      const individualResult: IActivityResultBreakdown = {
        name: activity.name,
        activity,
        result: activityUserCostResults,
        totalCosts: { cars: 0, trucks: 0, comboTrucks: 0 },
      };

      // Add individual activity result into alternative cost
      activityResults.push(individualResult);
    });

    altResult.activityResults = activityResults;

    // Collect user costs for each vehicle type from all activities
    altResult.vehicleCostsByType.cars = categoryBreakdown(activityResults, 'cars');
    altResult.vehicleCostsByType.trucks = categoryBreakdown(activityResults, 'trucks');
    altResult.vehicleCostsByType.comboTrucks = categoryBreakdown(activityResults, 'comboTrucks');
  }

  // console.log(altResult.alternative.name, altResult);
  return altResult;
};

//
// export const processAlternativeResult = function (activityResults: IActivityResultBreakdown[]) {
//   let queueLengthWarning = false;
//
//   let delayCosts = 0;
//   let vehicleCosts = 0;
//   let userTotalCosts = 0;
//   let agencyTotalCosts = 0;
//
//   activityResults.forEach((activityResult: IActivityResultBreakdown) => {
//
//   })
//
//
//   //TODO: this block
//   // function to calculate the cost breakdown of each vehicle group
//   const categoryBreakdown = function (dataArray, field, group) {
//     console.log(dataArray, field, group)
//     // return
//     // _.pluck(_.pluck(_.pluck(dataArray, 'result'), field), group).reduce(function (sum, value) {
//     //   return sum + value;
//     // }, 0) / 1000;
//     return 0
//   };
//
//   //breakdown each category of user costs
//
//   // if (userTotalCosts < returnObj.lowestAlternative.user.cost) {
//   //   lowestAlternative.user.alternativeName = alternative.name;
//   // }
//
//   // Fill out results for deterministic plot
//   returnObj.results.push({
//     alternative: alternative,
//     agencyCosts: agencyTotalCosts,
//     // agencyCosts: results.agencyCost_r[index].map(function (element) { return isNaN(element) ? 0 : element; }),
//     // activities: alternative.activities, //redundant - alternative.activities is already in alternative
//     userCosts: userTotalCosts,
//     // userDiscountedCosts: userDiscountedResults,
//     delayCosts: delayCosts,
//     vehicleCosts: vehicleCosts,
//     resultBreakdown: resultBreakdown,
//     queueLengthWarning: queueLengthWarning
//   });
//
//
//   /*
//   If costs are specified
//    */
// }
// else
// if (scenario.userCostsInAnalysis && scenario.userCostComputationMethod === "Specified") {
//   // Add together costs from each activity
//   alternative.activities.forEach(function (activity) {
//     userTotalCosts += activity.userWorkzoneCost || 0;
//   });
//
//   // Fill out results for deterministic plot
//   returnObj.results.push({
//     alternative: alternative,
//     agencyCosts: agencyTotalCosts,
//     userCosts: userTotalCosts,
//     vehicleCosts: 0,
//     delayCosts: 0,
//     queueLengthWarning: false,
//     resultBreakdown: null,
//   });
//
//   // If user costs aren't in the analysis
// } else if (!scenario.userCostsInAnalysis) {
//   returnObj.results.push({
//     alternative: alternative,
//     agencyCosts: agencyTotalCosts,
//     userCosts: 0,
//     vehicleCosts: 0,
//     delayCosts: 0,
//     queueLengthWarning: false,
//     resultBreakdown: null,
//   });
// }
//
// // goes into monteCarloResults ---====>>>> WHY??
// alternativeResults.push({
//   alternative: alternative,
//   agencyCosts: agencyTotalCosts,
//   userCosts: userTotalCosts,
//   resultBreakdown: resultBreakdown,
//   queueLengthWarning: queueLengthWarning,
//   delayCosts: 0,
//   vehicleCosts: 0,
// })
// }
//
// export const solver = function (activityOptions: any,
//                                 nProbabilisticVariables: number,
//                                 maxIterations: number,
//                                 alternatives: ILccaAlternative[],
// ) {
//
//   let iteration = 0;
//
//   while (iteration < maxIterations) {
//     console.log("Iterating", iteration, maxIterations);
//     iteration++;
//
//     completeLcca(scenario, options).then(lccaResults => {
//       let queueLengthWarning = false;
//
//
//       const alternativeResults: IAlternativeResult[] = [];
//
//       alternatives.forEach(function (alternative) {
//         const resultBreakdown: IActivityResultBreakdown = {
//           totalCosts: {cars: 0, trucks: 0, comboTrucks: 0},
//           result: null,
//           name: alternative.name + 'result breakdown'
//         };
//
//         let alternativeBreakdown: IActivityResultBreakdown[] = []; //array to access each alternative breakdown
//
//         let delayCosts = 0;
//         let vehicleCosts = 0;
//         let userTotalCosts = 0;
//         let agencyTotalCosts = 0;
//
//         const userDiscountedResults = 0;
//
//         // Add up agency costs:
//         alternative.activities.forEach(function (activity) {
//           agencyTotalCosts += calculateAgencyCost(activity.lineItems);
//         });
//
//         //Calculate user costs and gather
//
//         /*
//         If user costs are calculated
//          */
//
//
//         if (scenario.userCostsInAnalysis && scenario.userCostComputationMethod === "Calculated") {
//
//           // Add together costs from each activity
//           alternative.activities.forEach(function (activity) {
//
//             Object.keys(activityOptions).forEach(function (option) {
//               //@ts-ignore
//               activityOptions[option] = scenario.probabilistic[option] ? sampleDistribution(scenario.probabilistic[option]) : scenario[option];
//             });
//
//             const activityUserCostResults = calculateUserCosts(
//               scenario,
//               {
//                 activity: activity,
//                 appData: activityOptions
//               });
//
//             if (activityUserCostResults.queueLengthWarning === true) {
//               queueLengthWarning = true
//             }
//
//             const activityResults = activityUserCostResults;
//             delayCosts += (activityResults.delayCostsTotal / 1000);
//             vehicleCosts += (activityResults.vehicleCostsTotal / 1000);
//             userTotalCosts += (activityResults.totalCosts.total / 1000);
//             // userDiscountedResults += (activity.activityResults.discountedCosts.total / 1000);
//
//             /*
//
//             Calculate the costs of a single activity
//
//              */
//             const individualResult: IActivityResultBreakdown = {
//               'name': activity.name,
//               'result': activityUserCostResults,
//               totalCosts: {cars: 0, trucks: 0, comboTrucks: 0}
//             };
//
//             //add individual activity result into alternative cost
//             alternativeBreakdown.push(individualResult);
//           });
//
//
//           //TODO: this block
//           // function to calculate the cost breakdown of each vehicle group
//           const categoryBreakdown = function (dataArray, field, group) {
//             console.log(dataArray, field, group)
//             // return
//             // _.pluck(_.pluck(_.pluck(dataArray, 'result'), field), group).reduce(function (sum, value) {
//             //   return sum + value;
//             // }, 0) / 1000;
//             return 0
//           };
//
//           //breakdown each category of user costs
//           resultBreakdown.totalCosts.cars = categoryBreakdown(alternativeBreakdown, 'totalCosts', 'cars');
//           resultBreakdown.totalCosts.trucks = categoryBreakdown(alternativeBreakdown, 'totalCosts', 'trucks');
//           resultBreakdown.totalCosts.comboTrucks = categoryBreakdown(alternativeBreakdown, 'totalCosts', 'comboTrucks');
//
//           if (userTotalCosts < returnObj.lowestAlternative.user.cost) {
//             lowestAlternative.user.alternativeName = alternative.name;
//           }
//
//           // Fill out results for deterministic plot
//           returnObj.results.push({
//             alternative: alternative,
//             agencyCosts: agencyTotalCosts,
//             // agencyCosts: results.agencyCost_r[index].map(function (element) { return isNaN(element) ? 0 : element; }),
//             // activities: alternative.activities, //redundant - alternative.activities is already in alternative
//             userCosts: userTotalCosts,
//             // userDiscountedCosts: userDiscountedResults,
//             delayCosts: delayCosts,
//             vehicleCosts: vehicleCosts,
//             resultBreakdown: resultBreakdown,
//             queueLengthWarning: queueLengthWarning
//           });
//
//
//           /*
//           If costs are specified
//            */
//         } else if (scenario.userCostsInAnalysis && scenario.userCostComputationMethod === "Specified") {
//           // Add together costs from each activity
//           alternative.activities.forEach(function (activity) {
//             userTotalCosts += activity.userWorkzoneCost || 0;
//           });
//
//           // Fill out results for deterministic plot
//           returnObj.results.push({
//             alternative: alternative,
//             agencyCosts: agencyTotalCosts,
//             userCosts: userTotalCosts,
//             vehicleCosts: 0,
//             delayCosts: 0,
//             queueLengthWarning: false,
//             resultBreakdown: null,
//           });
//
//           // If user costs aren't in the analysis
//         } else if (!scenario.userCostsInAnalysis) {
//           returnObj.results.push({
//             alternative: alternative,
//             agencyCosts: agencyTotalCosts,
//             userCosts: 0,
//             vehicleCosts: 0,
//             delayCosts: 0,
//             queueLengthWarning: false,
//             resultBreakdown: null,
//           });
//         }
//
//         // goes into monteCarloResults ---====>>>> WHY??
//         alternativeResults.push({
//           alternative: alternative,
//           agencyCosts: agencyTotalCosts,
//           userCosts: userTotalCosts,
//           resultBreakdown: resultBreakdown,
//           queueLengthWarning: queueLengthWarning,
//           delayCosts: 0,
//           vehicleCosts: 0,
//         })
//       });
//
//
//       console.log(alternativeResults);
//       // monteCarloResults.push(alternativeResults);
//       //
//       // if (iteration % 20 == 0) {
//       //   console.log("Iteration", iteration, 'doing some MC thing');
//       //   const costs = monteCarloResults.map(function (Mresult) {
//       //     return Mresult.map(function (alternativeResult: any) {
//       //       return alternativeResult.agencyCosts[0] + alternativeResult.userCosts;
//       //     });
//       //   });
//       //   const tmp = [];
//       //   for (let i = 0; i < costs[0].length; i++) {
//       //     tmp.push(jstat.stdev(costs.map(function (cost) {
//       //       return cost[i];
//       //     })));
//       //   }
//       //   stds.push(tmp);
//       //
//       //   const slopes = [];
//       //
//       //   for (let i = 0; i < costs[0].length; i++) {
//       //     const xVals = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
//       //     const yVals = stds.slice(stds.length - 11, stds.length).map(function (element, index, array) {
//       //       if (index == 0) {
//       //         return false;
//       //       }
//       //       return Math.abs(element[i] - array[index - 1][i]);
//       //     }).filter(function (e) {
//       //       return e;
//       //     });
//       //
//       //     if (xVals.length == yVals.length) {
//       //       const slopeNShit = app.probabilityFunctions.leastSquares(xVals, yVals);
//       //       slopes[i] = Math.abs(slopeNShit.m);
//       //     }
//       //   }
//       //
//       //   const passed = slopes.filter(function (e) {
//       //     return e < .001;
//       //   });
//       //
//       //   if (passed.length == slopes.length) {
//       //     iteration = scenario.maxIterations;
//       //   }
//       //   console.log(stds)
//       // }
//       // return obj;
//     })
//   }
//
//   console.log('Main LCCA return', returnObj);
//
//   return returnObj
// };
//
// // function (e, data) {
// //   //Plot deterministic
// //   if (data.monteCarloResults.length === 1) {
// //     plotDeterministicResults(scenario, results);
// //     // Function to generate data array
// //     for (const i = 0; i < results.length; i++) {
// //       const alternativeActivities = results[i].alternative.activities;
// //       const alternativeData = [];
// //       const color = "";
// //       const colors = app.graphing.pickColors(alternativeActivities.length)
// //
// //       for (const j = 0; j < alternativeActivities.length; j++) {
// //         const resultsObject;
// //         // object contains activity info for graph
// //         resultsObject = {
// //           startYear: parseFloat(alternativeActivities[j].startYear),
// //           agencyConstructionCost: parseFloat(alternativeActivities[j].agencyConstructionCost),
// //           serviceLife: parseFloat(alternativeActivities[j].serviceLife_yrs),
// //           structuralLife: parseFloat(alternativeActivities[j].structuralLife_yrs),
// //           color: colors[j],
// //           category: alternativeActivities[j].name,
// //           userWorkzoneCost: parseFloat(alternativeActivities[j].userWorkzoneCost) //in case usercost specified
// //         };
// //         alternativeData.push(resultsObject);
// //       }
// //       cashFlowDiagram(alternativeData, i);
// //     }
// //
// //     if (err) {
// //       return $mainContainer.prepend('<div class="alert alert-danger">' + err + '</div>');
// //     }
// //   } else {
// //     obj.tabHeader = "Monte Carlo Results";
// //     dust.render('probResults', obj, function (err, out) {
// //       $("#update-container").remove();
// //       if (!app.isFullPageView) {
// //         $mainContainer.find('.tabContents').addClass('hidden');
// //       }
// //       ($('#lccaResultsContent').length === 0) ? $mainContainer.append(out) : $('#lccaResultsContent').replaceWith(out);
// //
// //       $mainContainer.fadeIn(0);
// //       app.bindInputFields();
// //       app.updateNavigationState();
// //       app.probabilityFunctions.graphing(obj);
// //       if (err) {
// //         return $mainContainer.prepend('<div class="alert alert-danger">' + err + '</div>');
// //       }
// //     });
// //   }
// // }
//
// // )
// // ;
// // }
